// Interfaces que definen la estructura de las respuestas
interface Detalles {
  data: Record<string, Record<string, {
    records: ComisionesDetallesResponse[],
    premiumNetaTotal: number,
    commissionTotal: number,
  }>>;
  currencyTotalCommission: Record<string, {
    commissionSCoaseguroTotal: number,
    commissionCoaseguroTotal: number
  }>;
  currencyTotalPremiumNeta: Record<string, {
    premiumNetaSCoaseguroTotal: number,
    premiumNetaCoaseguroTotal: number
  }>;
}

/**
 * Mapea los detalles de comisiones en una estructura agrupada por moneda y subramo.
 * También calcula los totales por tipo de prima y comisión.
 *
 * @param data - Lista de registros con los detalles de comisiones.
 * @returns Objeto estructurado con datos agrupados, comisiones totales y primas netas totales.
 */
export const mapComisionesDetalles = (data: ComisionesDetallesResponse[]): Detalles => {
  const groupedData: Record<string, Record<string, {
    records: ComisionesDetallesResponse[],
    premiumNetaTotal: number,
    commissionTotal: number,
  }>> = {};

  const currencyTotalCommission: Record<string, {
    commissionSCoaseguroTotal: number,
    commissionCoaseguroTotal: number
  }> = {};

  const currencyTotalPremiumNeta: Record<string, {
    premiumNetaSCoaseguroTotal: number,
    premiumNetaCoaseguroTotal: number
  }> = {};

  for (const record of data) {
    // Verificar si ya existe el grupo por moneda
    if (!groupedData[record.currencyName]) {
      /**
       * Si no existe, se inicializa el grupo por moneda, junto con el subgrupo por subramo.
       * También se inicializan los totales de primas netas y comisiones por moneda.
       */
      groupedData[record.currencyName] = {
        [record.slobDescription]: {
          records: [record],
          premiumNetaTotal: record.netPremium,
          commissionTotal: record.commission
        }
      };

      currencyTotalCommission[record.currencyName] = {
        commissionSCoaseguroTotal: record.commissionSCoinsurance,
        commissionCoaseguroTotal: record.commissionCoinsurance
      };

      currencyTotalPremiumNeta[record.currencyName] = {
        premiumNetaSCoaseguroTotal: record.netPremiumSCoinsurance,
        premiumNetaCoaseguroTotal: record.netPremiumCoinsurance
      };
    } else {
      /**
       * Si ya existe el grupo por moneda, se actualizan los totales generales
       * de comisiones y primas netas para esa moneda.
       */
      currencyTotalCommission[record.currencyName].commissionSCoaseguroTotal += record.commissionSCoinsurance;
      currencyTotalCommission[record.currencyName].commissionCoaseguroTotal += record.commissionCoinsurance;

      currencyTotalPremiumNeta[record.currencyName].premiumNetaSCoaseguroTotal += record.netPremiumSCoinsurance;
      currencyTotalPremiumNeta[record.currencyName].premiumNetaCoaseguroTotal += record.netPremiumCoinsurance;

      // Verificar si ya existe el grupo por subramo dentro de la moneda
      if (!groupedData[record.currencyName][record.slobDescription]) {
        /**
         * Si no existe el subgrupo, se crea con el registro actual y sus valores iniciales.
         */
        groupedData[record.currencyName][record.slobDescription] = {
          records: [record],
          premiumNetaTotal: record.netPremium,
          commissionTotal: record.commission
        };
      } else {
        /**
         * Si el subgrupo ya existe, se agrega el nuevo registro y se actualizan los totales.
         */
        const subGroup = groupedData[record.currencyName][record.slobDescription];
        subGroup.records.push(record);
        subGroup.premiumNetaTotal += record.netPremium;
        subGroup.commissionTotal += record.commission;
      }
    }
  }

  return { data: groupedData, currencyTotalCommission, currencyTotalPremiumNeta };
};

// Interfaces para detalles por cobro
interface DetallesPorCobro {
  data: Record<string, {
    records: ComisionesDetallesPorCobroResponse[],
    premiumNetaTotal: number,
    commissionTotal: number,
    participationTotal: number
  }>;
  primaNetaTotal: number;
  participacionTotal: number;
  comisionPrimaNetaTotal: number;
}

/**
 * Mapea los detalles de comisiones por cobro agrupándolos por subramo.
 * También calcula totales generales de prima neta, participación y comisión.
 *
 * @param data - Lista de registros de comisiones por cobro.
 * @returns Objeto con los datos agrupados por subramo y los totales generales.
 */
export const mapComisionesDetallesPorCobro = (data: ComisionesDetallesPorCobroResponse[]): DetallesPorCobro => {
  const groupedData: Record<string, {
    records: ComisionesDetallesPorCobroResponse[],
    premiumNetaTotal: number,
    commissionTotal: number,
    participationTotal: number
  }> = {};

  let primaNetaTotal = 0;
  let participacionTotal = 0;
  let comisionPrimaNetaTotal = 0;

  for (const record of data) {
    // Verificar si ya existe el grupo por subramo
    if (!groupedData[record.slobDescription]) {
      /**
       * Si no existe el subgrupo, se inicializa con el registro actual y sus valores.
       */
      groupedData[record.slobDescription] = {
        records: [record],
        premiumNetaTotal: record.coverageValue,
        participationTotal: record.coverageValuePart,
        commissionTotal: record.comision
      };
    } else {
      /**
       * Si el subgrupo ya existe, se agrega el nuevo registro y se actualizan los totales.
       */
      const subGroup = groupedData[record.slobDescription];
      subGroup.records.push(record);
      subGroup.premiumNetaTotal += record.coverageValue;
      subGroup.participationTotal += record.coverageValuePart;
      subGroup.commissionTotal += record.comision;
    }

    // Acumular totales generales
    primaNetaTotal += record.coverageValue;
    participacionTotal += record.coverageValuePart;
    comisionPrimaNetaTotal += record.comision;
  }

  return { data: groupedData, primaNetaTotal, participacionTotal, comisionPrimaNetaTotal };
};


// Interfaces de las respuestas
export interface ComisionesDetallesResponse {
  producerName: string;
  slob: number;
  policyNumber: string;
  invoiceNumber: number;
  netPremium: number;
  grossPremium: number;
  commission: number;
  commissionRate: number;
  wDate: Date;
  rCurrency: number;
  coverageGroupDescription: string;
  coinsurancePercent: number;
  commissionCredito: number;
  currency: number;
  clientName: string;
  currencyName: string;
  slobDescription: string;
  dateFrom: Date;
  dateTo: Date;
  document: string;
  netPremiumCoinsurance: number;
  commissionCoinsurance: number;
  netPremiumSCoinsurance: number;
  commissionSCoinsurance: number;
}

export interface ComisionesDetallesPorCobroResponse {
  producerName: string;
  slob: number;
  slobDescription: string;
  policyNumber: string;
  invoiceNumber: number;
  coverageValue: number;
  grossPremium: number;
  coverageValuePart: number;
  comision: number;
  wDate: Date;
  gCovDescription: string;
  commissionPercentage: number;
  clientName: string;
  dateFrom: Date;
  dateTo: Date;
  document: string;
}
